import { type FC } from "react";
import { Input } from "~/shared/ui/input";
import { Select, SelectContent, SelectItem, SelectValue } from "~/shared/ui/select";
import { cn } from "~/shared/lib/utils";
import { FILTER_FIELD_CLASS, FilterSelectTrigger } from "./filter-field-primitives";

/** Parses stored email_domain value: "include:syntez25.com" | "exclude:gmail.com" | undefined */
export function parseEmailDomainValue(val: unknown): { mode?: "include" | "exclude"; domain?: string } {
    if (!val || typeof val !== "string") return {};
    if (val.startsWith("include:")) return { mode: "include", domain: val.slice(8) };
    if (val.startsWith("exclude:")) return { mode: "exclude", domain: val.slice(8) };
    return {};
}

export const EmailDomainFilter: FC<{
    value: string;
    onChange: (value: string | undefined) => void;
    labels?: { include?: string; exclude?: string; placeholder?: string };
    hasValue?: boolean;
}> = ({ value, onChange, labels }) => {
    const parsed = parseEmailDomainValue(value);
    const mode = parsed.mode ?? "__all__";
    const domain = parsed.domain ?? "";
    const defaultDomain = labels?.placeholder ?? "domain.com";
    const isActive = mode !== "__all__";

    return (
        <div className="flex gap-1.5">
            <Select
                value={mode}
                onValueChange={(v) => {
                    if (v === "__all__") {
                        onChange(undefined);
                    } else {
                        onChange(`${v}:${domain || defaultDomain}`);
                    }
                }}
            >
                <FilterSelectTrigger
                    className={cn(
                        "h-9 w-[135px] shrink-0 focus-visible:border-input focus-visible:ring-0",
                        FILTER_FIELD_CLASS,
                    )}
                >
                    <SelectValue />
                </FilterSelectTrigger>
                <SelectContent position="popper" align="start">
                    <SelectItem value="__all__">—</SelectItem>
                    <SelectItem value="include">{labels?.include ?? "Only"}</SelectItem>
                    <SelectItem value="exclude">{labels?.exclude ?? "Exclude"}</SelectItem>
                </SelectContent>
            </Select>
            <Input
                value={isActive ? domain : ""}
                disabled={!isActive}
                onChange={(e) => {
                    const d = e.target.value.trim().toLowerCase();
                    onChange(`${mode}:${d}`);
                }}
                placeholder={labels?.placeholder ?? "domain.com"}
                className={cn("h-9 flex-1 !px-3", FILTER_FIELD_CLASS, !isActive && "opacity-50")}
            />
        </div>
    );
};
